import { useState } from "react";
import { cn } from "@/lib/cn";

/**
 * Avatar — renders a cached profile photo when available, else a colored
 * initials chip derived deterministically from a stable key (so the same
 * account/peer always gets the same color). Image auth is cookie-based, so a
 * plain <img src="/api/.../avatar"> authenticates automatically; on 404/error
 * we fall back to initials.
 */

// Muted palette that reads well on the dark theme. Index chosen by a hash of
// the color key so colors are stable per entity, not random per render.
const PALETTE = [
  "#6366f1", // indigo
  "#8b5cf6", // violet
  "#0ea5e9", // sky
  "#14b8a6", // teal
  "#10b981", // emerald
  "#f59e0b", // amber
  "#ef4444", // red
  "#ec4899", // pink
  "#64748b", // slate
];

function initialsOf(name: string): string {
  const parts = name.replace(/^@/, "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

function colorFor(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length]!;
}

export interface AvatarProps {
  /** Display name used for the initials fallback + alt text. */
  name: string;
  /** Avatar image URL, or null/undefined to render initials directly. */
  src?: string | null;
  /** Stable key for the fallback color (e.g. account/peer id). Defaults to name. */
  colorKey?: string;
  /** Pixel size of the (square) avatar. */
  size?: number;
  className?: string;
}

export function Avatar({ name, src, colorKey, size = 32, className }: AvatarProps) {
  const [failed, setFailed] = useState(false);
  const dim = { width: size, height: size };

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={name}
        width={size}
        height={size}
        loading="lazy"
        onError={() => setFailed(true)}
        className={cn("shrink-0 rounded-full bg-bg-elevated object-cover", className)}
        style={dim}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-flex shrink-0 select-none items-center justify-center rounded-full font-medium text-white",
        className,
      )}
      style={{
        ...dim,
        backgroundColor: colorFor(colorKey ?? name),
        fontSize: Math.round(size * 0.4),
      }}
    >
      {initialsOf(name)}
    </span>
  );
}
Avatar.displayName = "Avatar";
